<template>
  <div
    v-if="$route.matched.length > 0 && themeLoaded"
    id="app"
    class="app v-self-service-app"
  >
    <div
      v-if="$route.meta && $route.meta.public"
      class="public_wrapper"
    >
      <router-view />
    </div>
    <secure-layout
      v-else
      :iframeView="!!$route.matched.find(route => route.meta.iframe)"
    >
      <router-view :key="currentProject" />
    </secure-layout>
    <div class="svg-ui-syntax-sprites">
      <sprite-svg />
    </div>
  </div>
  <div
    v-else
    class="app app--is-loading"
  >
    <el-loading
      :color="'brand-primary'"
      :size="'48'"
    />
  </div>
</template>

<script>
  import { findLast } from 'lodash-es';
  import appConfig from '@/common/appConfig';
  import createStore from '@/common/store/createStore';
  import SecureLayout from '@/self-service/layouts/SelfServiceSecureLayout.vue';
  import leaveCounterMixin from '@/common/mixins/leaveCounter.mixin';
  import { storeModulesGroups } from '@/common/store/modules';
  import SpriteSvg from '@/common/components/SpriteSvg.vue';
  import useAuthStore from '@/common/store/pinia/auth';
  import { mapState, mapStores } from 'pinia';
  import useThemeStore from '@/common/store/pinia/theme';
  import usePostMessage from '@/common/composables/usePostMessage';
  import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
  import { applyColorScheme } from '@/common/utils/applyColors';
  import useAppModeStore from '@/common/store/pinia/appMode';
  import useTotpSettingsStore from '@/common/store/pinia/totpSettings';

  const { selfservice: selfServiceModules } = storeModulesGroups;
  const { sendPostMessage } = usePostMessage();

  export default {
    name: 'SelfServiceApp',
    metaInfo() {
      if (!this.themeLoaded) {
        return {
          title: this.$t('Loading...'),
          titleTemplate: '%s',
          link: [{ rel: 'icon', href: 'data:,' }]
        };
      }
      const translate = this.$t.bind(this);

      const route = this.$route.matched.find(item => item.name === this.$route.name) || this.$route;

      const routeWithTitle = findLast(this.$route.matched, item => item.meta?.title);

      const title = typeof routeWithTitle?.meta.title === 'function' && routeWithTitle.meta.title(translate);

      const { productTitle, productName } = this.themeData;
      const appTitle = productTitle || productName;

      return {
        title: title || route.meta.title || route.name || '',
        titleTemplate: (titleChunk) => titleChunk ? `${titleChunk} - ${appTitle}` : appTitle,
        link: this.favicon
      };
    },
    components: {
      SecureLayout,
      SpriteSvg
    },
    mixins: appConfig.disable_idle_timeout ? [] : [leaveCounterMixin],
    created() {
      createStore(selfServiceModules);
      const start = () => this.initApp();
      this.shouldWaitForRouterReady ? this.$router.onReady(start) : start();
    },
    computed: {
      ...mapStores(useAuthStore, useThemeStore),
      ...mapState(useAuthStore, ['isLoggedIn', 'accessDisabled', 'currentProject', 'currentDomain', 'domainMode']),
      ...mapState(useThemeStore, ['schemesState', 'themeData', 'themeLoaded']),
      ...mapState(useAppModeStore, ['shouldInjectThemeStyles', 'shouldWaitForRouterReady']),
      favicon() {
        return [{ rel: 'icon', href: this.themeData.favicon }];
      },
      themeCssVariables() {
        if (!this.shouldInjectThemeStyles || !this.themeLoaded) {
          return null;
        }
        return this.schemesState[this.themeData.colorScheme] ?? null;
      },
    },
    watch: {
      isLoggedIn: {
        handler(val) {
          if (val) {
            sendPostMessage('authSuccess');
            useTotpSettingsStore().fetchTotpSettings();
          } else {
            sendPostMessage('logout');
            useTotpSettingsStore().resetTotpSettings();
          }
        },
        immediate: true
      },
      accessDisabled: {
        immediate: true,
        handler(newVal, oldVal) {
          if (this.$route.name === 'Login') {
            return;
          }
          if (newVal && !oldVal && this.$route.name !== 'errorAccessDisabled') {
            this.$router.push({ name: 'errorAccessDisabled' });
          } else if (!newVal && oldVal) {
            this.$router.push('/');
          }
        }
      },
      themeCssVariables(value) {
        if (value) {
          applyColorScheme(value);
        }
      }
    },
    methods: {
      fetchTheme() {
        return this.themeStore.fetchTheme(this.currentDomain);
      },
      initApp() {
        const domainName = this.$route.name === 'Login' && !this.$route.params.domainName
          ? DEFAULT_DOMAIN_NAME
          : this.$route.params.domainName || localStorage.getItem('domain');
        this.authStore.setDomain(domainName);
        if (this.$route.query.project_id) {
          this.authStore.setProject(this.$route.query.project_id);
        }
        this.fetchTheme();
      }
    }
  };
</script>

<style lang="scss" scoped>
  .app {
    height: 100%;

    &--is-loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
